import React from 'react';

import i18n from '@client/utils/i18n';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

export interface DmxChannelMapperRow {
    channelId: string;
    name: string;
    currentDmxAddress: number;
    resolution: number;
}

export interface DmxChannelMapperProps {
    channels: DmxChannelMapperRow[];
    value: Record<string, number | undefined>;
    onChange: (value: Record<string, number | undefined>) => void;
    onTest: (channelId: string, dmxAddress: number, resolution: number) => void;
    testingChannelId: string | null;
    takenAddresses?: number[];
}

function isValidAddress(v: number | undefined): v is number {
    return v !== undefined && Number.isInteger(v) && v >= 1 && v <= 512;
}

function DmxChannelMapper({ channels, value, onChange, onTest, testingChannelId, takenAddresses }: DmxChannelMapperProps) {
    function handleChange(channelId: string, raw: string) {
        const parsed = raw === '' ? undefined : parseInt(raw, 10);
        onChange({ ...value, [channelId]: isNaN(parsed as number) ? undefined : parsed });
    }

    const addressCount = Object.values(value).reduce<Record<number, number>>((acc, v) => {
        if (v !== undefined) {
            acc[v] = (acc[v] ?? 0) + 1;
        }
        return acc;
    }, {});

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {channels.map((ch) => {
                const v = value[ch.channelId];
                const valid = isValidAddress(v);
                const taken = valid && takenAddresses?.includes(v) === true;
                const duplicate = valid && (addressCount[v] ?? 0) > 1;
                const hasWarning = taken || duplicate;
                const testing = testingChannelId === ch.channelId;

                let helperText: string | undefined;
                if (v !== undefined && !valid) {
                    helperText = '1 – 512';
                } else if (taken) {
                    helperText = 'Already in use';
                } else if (duplicate) {
                    helperText = 'Duplicated DMX channel';
                }

                return (
                    <Box key={ch.channelId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap>{ch.name}</Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={300}>
                                {'DMX '}
                                <Box component="span" fontWeight={700}>
                                    {ch.currentDmxAddress}
                                </Box>
                                {' · '}
                                <Box component="span" fontWeight={700}>
                                    {ch.resolution}
                                </Box>
                                {' bit'}
                            </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    New DMX
                                </Typography>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={v ?? ''}
                                    onChange={(e) => handleChange(ch.channelId, e.target.value)}
                                    inputProps={{ min: 1, max: 512 }}
                                    error={v !== undefined && !valid}
                                    sx={{ width: 90 }}
                                />
                            </Box>
                            {helperText && (
                                <Typography variant="caption" sx={{ color: hasWarning ? 'warning.main' : 'error.main', display: 'block', mt: 0.25 }}>
                                    {helperText}
                                </Typography>
                            )}
                        </Box>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!valid || hasWarning || testing}
                            onClick={() => valid && !hasWarning && onTest(ch.channelId, v, ch.resolution)}
                            startIcon={testing ? <CircularProgress size={14} /> : <WbIncandescentIcon />}
                            sx={{ flexShrink: 0, mt: 0.5 }}
                        >
                            {i18n('test')}
                        </Button>
                    </Box>
                );
            })}
        </Box>
    );
}

export default DmxChannelMapper;
